import { StrictMode } from 'react';
import { createRoot } from 'react-dom/client';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { retryWaitMs } from './api/client';
import { App } from './App';
import { initDensity } from './theme/density';
import './theme/fonts.css';
import './theme/tokens.css';
import './theme/global.css';

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      retryDelay: (failureCount, error) => retryWaitMs(
        error,
        Math.min(1_000 * (2 ** failureCount), 30_000),
      ),
    },
  },
});

// Density lives on the root element so tokens.css can swap row heights
// before the first paint. localStorage may be unavailable; init never throws.
initDensity(document.documentElement, typeof localStorage === 'undefined' ? null : localStorage);

// A tab loaded before a deploy references lazy chunks the server no longer
// carries; Vite surfaces that as vite:preloadError. One guarded reload per
// tab fetches the current build instead of leaving dead destinations
// (observed in production 2026-08-28 after same-day cutovers).
window.addEventListener('vite:preloadError', (event) => {
  event.preventDefault();
  const key = 'aleph.reloadedForPreloadError';
  try {
    if (sessionStorage.getItem(key) === '1') return;
    sessionStorage.setItem(key, '1');
  } catch {
    // Storage unavailable: reload anyway, the loop guard just does not apply.
  }
  window.location.reload();
});

const container = document.getElementById('root');
if (!container) {
  throw new Error('Root element #root is missing from index.html');
}

createRoot(container).render(
  <StrictMode>
    <QueryClientProvider client={queryClient}>
      <App />
    </QueryClientProvider>
  </StrictMode>,
);
